import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import {
  SchoolPassScope,
  passCoversCourse,
  passLandingBundleId,
  schoolPassService,
  shouldHidePrices,
} from '../services/schoolPassService';
import { UserRole } from '../types';
import { reportError } from '../lib/errorHandling';

const EMPTY: SchoolPassScope[] = [];

interface SchoolPasses {
  passes: SchoolPassScope[];
  isLoading: boolean;
  /** Does an active pass cover this course? False while loading — surfaces
   *  degrade to the normal payment path, never the other way round. */
  coversCourse: (courseId: string) => boolean;
  /** Re-read after a redeem so ownership surfaces update without a reload. */
  refresh: () => void;
  /** Students holding a pass see no prices (see shouldHidePrices). */
  hidePrices: boolean;
  /** The pack a pass-holding student starts on after sign-in, if any. */
  landingBundleId: string | null;
}

/**
 * The caller's active school passes. Fetches nothing when the tenant doesn't
 * have school_passes or nobody is signed in; errors are best-effort (a learner
 * whose pass read failed sees the ordinary buy path, not a broken page).
 */
export function useSchoolPasses(): SchoolPasses {
  const { user } = useAuth();
  const { features } = useTenant();
  const enabled = !!features.school_passes && !!user;
  const [passes, setPasses] = useState<SchoolPassScope[]>(EMPTY);
  // Starts true when a read is about to happen, so the first render doesn't
  // claim "no passes" before the fetch has even begun (Home's voucher prompt
  // would flash for a covered learner).
  const [isLoading, setIsLoading] = useState(enabled);
  const [generation, setGeneration] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setPasses(EMPTY);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    schoolPassService
      .getMyActivePasses()
      .then((result) => {
        if (!cancelled) setPasses(result);
      })
      .catch((err) => reportError('useSchoolPasses', err))
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, generation]);

  const coversCourse = useCallback(
    (courseId: string) => passCoversCourse(passes, courseId),
    [passes],
  );
  const refresh = useCallback(() => setGeneration((g) => g + 1), []);

  const isStudent = user?.role === UserRole.STUDENT;
  const hidePrices = shouldHidePrices(isStudent, passes, isLoading);
  const landingBundleId = isStudent ? passLandingBundleId(passes) : null;

  return { passes, isLoading, coversCourse, refresh, hidePrices, landingBundleId };
}
